import { useEffect, useRef, useState, ReactNode } from 'react';
import { hexToRgb, rgbToHex, rgbToHsv, hsvToRgb, Materials } from '@worldify/shared';
import {
  ChevronDown, ChevronRight, Zap, Wrench, Sliders, Palette, Droplet, Waves,
  Sparkles, Wind, Sun, Moon, Clock, Grid3x3, Lightbulb, Sunrise, Trash2, Timer, Footprints,
} from 'lucide-react';
import { runGenBench, deviceInfo, type GenBenchRow } from '../game/debug/genBench';
import { perfStats, WORLD_STAGES, type PerfSection, type PerfSnapshot } from '../game/debug/PerformanceStats';
import {
  useGameStore, TERRAIN_DEBUG_MODE_NAMES, TERRAIN_DEBUG_MODE_ORDER, type TerrainDebugMode,
  BUILD_PREVIEW_LIGHTING_ORDER, BUILD_PREVIEW_LIGHTING_LABELS, type BuildPreviewLighting,
  type EnvironmentSettings, type DayNightKeyframe, type DayNightConfig, type VoxelDebugToggles,
} from '../state/store';
import { Dropdown, type DropdownOption } from './Dropdown';
import { textureCache } from '../game/material/TextureCache';
import { setTerrainDebugMode as setShaderDebugMode, applyLightFillSettings, applyBlockLightSettings, TERRAIN_DEBUG_MODES } from '../game/material/TerrainMaterial';
import { setLodDebugEnabled, lodDebugCss, lodDebugLabel, skirtKindCss, SKIRT_KIND_NAMES, LOD_DEBUG_COLORS, type LodTag } from '../game/material/LodDebugMaterials';
import { applyEnvironmentSettings } from '../game/scene/Lighting';
import { formatTimeOfDay, getDayPhaseLabel, invalidateDayNight } from '../game/scene/DayNightCycle';
import { getPhysicsHud, type PhysicsHud } from '../state/transient';
import {
  cycleQualityLevel, QUALITY_LABELS, QUALITY_LEVELS, QUALITY_ROWS, MSAA_OPTIONS,
  qualityMatchesPreset, type QualityLevel,
} from '../game/quality/QualityPresets';
import { applyQualityPatch, syncQualityToStore } from '../game/quality/QualityManager';
import { getCamera } from '../game/scene/camera';

// ============== Collapsible Section Component ==============

interface SectionProps {
  title: string;
  icon?: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
  color?: string;
}

function Section({ title, icon, isOpen, onToggle, children, color = 'green' }: SectionProps) {
  const colorClasses: Record<string, string> = {
    green: 'text-green-500 hover:text-green-300',
    yellow: 'text-yellow-500 hover:text-yellow-300',
    cyan: 'text-cyan-500 hover:text-cyan-300',
  };

  return (
    <div className="mt-2">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-1 px-0 text-left cursor-pointer ${colorClasses[color] || colorClasses.green}`}
      >
        <span className="font-bold flex items-center gap-1.5">{icon}{title}</span>
        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      {isOpen && (
        <div className="pl-2 border-l border-green-500/30">
          {children}
        </div>
      )}
    </div>
  );
}

// ============== Performance: frame strip + ranked stage table ==============

/**
 * ONE COLOUR PER MAIN-THREAD TERM, shared by the CPU bar's segments and the stage table's names, so
 * the bar and the table explain each other without a legend: every World stage is green (they are
 * the green segment's parts), render cyan, physics purple, other-update grey, off-frame orange.
 */
const STAGE_COLOR = {
  world: 'text-green-400', render: 'text-cyan-300', physics: 'text-purple-300', other: 'text-gray-400', offFrame: 'text-orange-400',
} as const;
const BAR_COLOR = {
  world: 'bg-green-500', render: 'bg-cyan-400', physics: 'bg-purple-400', other: 'bg-gray-500', offFrame: 'bg-orange-400',
} as const;

/**
 * THE FRAME STRIP. Two bars on ONE time axis — the main thread and the GPU — because they run in
 * parallel and the interval is set by whichever reaches it, not by their sum (there is no sum to
 * draw). Ticks: the refresh period (white — the CPU and GPU budget) and the World's allowance (the
 * yellow line under the bar, 0 → allowance; the World segment is drawn first so the two read against
 * each other). The CPU bar is World / Render / Physics / other-update / off-frame, then idle to the
 * interval.
 */
function FrameStrip({ p }: { p: PerfSnapshot }) {
  const cpu = p.gameUpdate + p.offFrame;
  const axis = Math.max(p.frameInterval, p.gpu, cpu, 2 * p.refreshMs);
  const pct = (ms: number) => `${Math.max(0, Math.min(100, (100 * ms) / axis))}%`;
  const other = Math.max(0, p.gameUpdate - p.voxelUpdate - p.render - p.physics);
  const segs: Array<[number, string, string]> = [
    [p.voxelUpdate, BAR_COLOR.world, `World ${p.voxelUpdate.toFixed(1)} ms (green stages below)`],
    [p.render, BAR_COLOR.render, `Render — CPU submit ${p.render.toFixed(1)} ms`],
    [p.physics, BAR_COLOR.physics, `Physics ${p.physics.toFixed(1)} ms`],
    [other, BAR_COLOR.other, `Other update (players, environment, map, camera) ${other.toFixed(1)} ms`],
    [p.offFrame, BAR_COLOR.offFrame, `Off-frame — worker replies, socket payloads, IDB ${p.offFrame.toFixed(1)} ms`],
  ];
  const Tick = ({ ms, cls, title }: { ms: number; cls: string; title: string }) => (
    <div className={`absolute top-0 bottom-0 w-px ${cls}`} style={{ left: pct(ms) }} title={title} />
  );
  return (
    <div className="mb-1">
      <div className="flex items-center gap-1">
        <span className="w-7 shrink-0" title="Main thread per frame: update callback + off-frame handlers. Empty to the right = idle. White tick = refresh period (the budget); yellow line = the World's allowance">CPU</span>
        <div className="relative flex-1 h-2 bg-black/40 rounded-sm overflow-hidden flex">
          {segs.map(([ms, cls, title], i) => <div key={i} className={`${cls} h-full`} style={{ width: pct(ms) }} title={title} />)}
          <Tick ms={p.refreshMs} cls="bg-white/60" title={`Refresh period ${p.refreshMs.toFixed(1)} ms — the CPU and GPU budget`} />
          <div className="absolute bottom-0 h-px bg-yellow-400" style={{ left: 0, width: pct(p.voxelBudget) }}
            title={`World allowance ${p.voxelBudget.toFixed(1)} ms — the green segment should end before this line`} />
        </div>
        <span className={`w-9 text-right ${cpu > p.refreshMs ? 'text-red-400' : ''}`}>{cpu.toFixed(1)}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="w-7 shrink-0" title="GPU time of the render (timer query). Runs in parallel with the CPU; on an iGPU heavy CPU work still lengthens it (shared power + bandwidth)">GPU</span>
        <div className="relative flex-1 h-2 bg-black/40 rounded-sm overflow-hidden">
          <div className="bg-fuchsia-400 h-full" style={{ width: pct(p.gpu) }} />
          <Tick ms={p.refreshMs} cls="bg-white/60" title={`Refresh period ${p.refreshMs.toFixed(1)} ms`} />
        </div>
        <span className={`w-9 text-right ${p.gpu > p.refreshMs ? 'text-red-400' : ''}`}>{p.gpu > 0 ? p.gpu.toFixed(1) : 'n/a'}</span>
      </div>
    </div>
  );
}

/** The ↺ in the header — module scope, so the component's store-destructured `perfStats` cannot shadow the singleton. */
const resetPerfPeaks = (): void => perfStats.resetSessionPeaks();

/** Everything the panel may rank — name shown, and the CPU-bar term it belongs to (its colour). */
const STAGE_LABEL: Partial<Record<PerfSection, string>> = {
  lightSun: 'l.sun', lightNbr: 'l.nbr', lightCoarse: 'l.crs', lightEdit: 'l.edit',
};
const RANKED: ReadonlyArray<[PerfSection, string, keyof typeof STAGE_COLOR]> = [
  ...WORLD_STAGES.map((s): [PerfSection, string, keyof typeof STAGE_COLOR] => [s, STAGE_LABEL[s] ?? s, 'world']),
  ['render', 'render', 'render'], ['physics', 'physics', 'physics'],
  // offFrame's CHILDREN, not the aggregate: the orange CPU-bar segment is the total; the table says who.
  ['offGen', 'offgen', 'offFrame'], ['offMesh', 'offmsh', 'offFrame'],
  ['offNet', 'offnet', 'offFrame'], ['offIdb', 'offidb', 'offFrame'],
  ['debugViz', 'overlay', 'other'],
];

/**
 * Top 8 by SESSION-MAX peak, two per row as `name avg/peak`. Ranked and displayed by the worst frame
 * each stage has ever had, so the order settles within seconds of the worst case and the table is a
 * record of where the jank was — ↺ forgets it (after the cold load's one-off spikes, say). Names carry
 * their CPU-bar colour; a value goes yellow when the peak is more than twice the average and above
 * 2 ms — the jank signature.
 */
function StageTable({ p }: { p: PerfSnapshot }) {
  const rows = RANKED.filter(([k]) => p.peak[k] > 0.05).sort((a, b) => p.peak[b[0]] - p.peak[a[0]]).slice(0, 8);
  return (
    <div>
      <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-x-2 text-[10px]">
        {rows.map(([k, name, group]) => {
          const avg = p[k], peak = p.peak[k];
          const spiky = peak > 2 && peak > 2 * avg;
          return (
            <div key={k} className="contents">
              <div className={STAGE_COLOR[group]} title={`${k}: average over the last 60 frames / worst frame this session, ms`}>{name}</div>
              <div className={spiky ? 'text-yellow-400' : ''}>{avg.toFixed(1)}/{peak >= 100 ? peak.toFixed(0) : peak.toFixed(1)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============== Slider Component ==============

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
}

function Slider({ label, value, min, max, step = 0.01, onChange, formatValue }: SliderProps) {
  const displayValue = formatValue ? formatValue(value) : value.toFixed(2);
  
  return (
    <div className="flex flex-col gap-0.5 mb-1">
      <div className="flex justify-between text-xs">
        <span>{label}</span>
        <span className="text-yellow-400">{displayValue}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-green-900 rounded-lg appearance-none cursor-pointer accent-yellow-400"
      />
    </div>
  );
}

// ============== Color Picker Component ==============

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Custom colour picker: a swatch that opens a popover with a hex field, a hue slider, and a
 * 2D saturation/value square. Emits `#rrggbb` hex strings (same contract as the old native input).
 */
function ColorPicker({ label, value, onChange }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const rgb = hexToRgb(value);
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
  const svRef = useRef<HTMLDivElement>(null);

  const emitHsv = (h: number, s: number, v: number) => {
    const c = hsvToRgb(h, s, v);
    onChange(rgbToHex(c.r, c.g, c.b));
  };

  const handleSV = (e: React.PointerEvent) => {
    const el = svRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const s = clamp01((e.clientX - rect.left) / rect.width);
    const v = 1 - clamp01((e.clientY - rect.top) / rect.height);
    emitHsv(hsv.h, s, v);
  };

  const hc = hsvToRgb(hsv.h, 1, 1);
  const hueHex = rgbToHex(hc.r, hc.g, hc.b);

  return (
    <div className="mb-1">
      <div className="flex items-center justify-between">
        <span className="text-xs">{label}</span>
        <button
          onClick={() => setOpen((o) => !o)}
          className="w-8 h-5 rounded border border-white/30 cursor-pointer"
          style={{ backgroundColor: value }}
          aria-label={`${label} colour`}
        />
      </div>
      {open && (
        <div className="mt-1 p-2 rounded-lg bg-black/80 border border-white/15 flex flex-col gap-2">
          {/* Saturation / value square */}
          <div
            ref={svRef}
            className="relative w-full h-24 rounded cursor-crosshair touch-none"
            style={{
              backgroundColor: hueHex,
              backgroundImage:
                'linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)',
            }}
            onPointerDown={(e) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); handleSV(e); }}
            onPointerMove={(e) => { if (e.buttons === 1) handleSV(e); }}
          >
            <span
              className="absolute w-3 h-3 -ml-1.5 -mt-1.5 rounded-full border-2 border-white shadow pointer-events-none"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: value }}
            />
          </div>
          {/* Hue slider */}
          <input
            type="range" min={0} max={360} step={1} value={Math.round(hsv.h)}
            onChange={(e) => emitHsv(parseInt(e.target.value, 10), hsv.s || 1, hsv.v || 1)}
            className="w-full h-2 rounded appearance-none cursor-pointer"
            style={{ background: 'linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)' }}
          />
          {/* Hex field */}
          <input
            type="text" value={value}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v.toLowerCase());
            }}
            className="bg-black/60 border border-white/20 rounded text-xs px-1.5 py-0.5 text-white font-mono"
          />
        </div>
      )}
    </div>
  );
}

// ============== Select Component ==============

interface SelectProps<T> {
  label: string;
  value: T;
  options: { label: string; value: T }[];
  onChange: (value: T) => void;
}

function Select<T extends string | number>({ label, value, options, onChange }: SelectProps<T>) {
  return (
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange((typeof value === 'number' ? parseInt(e.target.value) : e.target.value) as T)}
        className="bg-black/50 border border-green-500/50 rounded text-xs px-1 py-0.5 text-yellow-400"
      >
        {options.map((opt) => (
          <option key={String(opt.value)} value={opt.value as string | number}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ============== Toggle Component ==============

interface ToggleProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

function Toggle({ label, value, onChange }: ToggleProps) {
  return (
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs">{label}</span>
      <button
        onClick={() => onChange(!value)}
        className={`w-10 h-5 rounded-full relative ${
          value ? 'bg-green-600' : 'bg-gray-600'
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            value ? 'left-5' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

// ============== Segmented single-select row ==============

interface SegmentedRowProps {
  label: string;
  segments: { label: string }[];
  active: number; // index of the active segment, or -1
  onSelect: (index: number) => void;
}

/** A labelled row of mutually-exclusive segment buttons (Off/On, 2/4/6/8, …). */
function SegmentedRow({ label, segments, active, onSelect }: SegmentedRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="text-xs whitespace-nowrap">{label}</span>
      <div className="flex gap-0.5">
        {segments.map((seg, i) => (
          <button
            key={seg.label}
            onClick={() => onSelect(i)}
            className={`px-1.5 py-0.5 text-[11px] rounded ${
              i === active ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {seg.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============== Gen Timing Section ==============

/**
 * On-device generation timing. Runs the boot-stage bench (construct → cold column-gen → warm gen →
 * ingest) with a fixed seed/column so 28, 70 and 70-terrain-only are directly comparable on the same
 * phone. Self-contained (own running state). The cold column-gen row is the dominant "time to first
 * chunk bound" stage.
 *
 * Rendered as a block INSIDE the Debug section rather than a top-level section of its own: it's a
 * one-button on-device tool used a few times per device, and a whole collapsible section for it cost
 * more panel height than the results it prints.
 */
function GenTimingBench() {
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState<GenBenchRow[]>([]);

  const run = async () => {
    if (running) return;
    setRunning(true);
    setRows([]);
    const acc: GenBenchRow[] = [];
    try {
      await runGenBench((row) => { acc.push(row); setRows([...acc]); });
      // eslint-disable-next-line no-console
      console.log('[genBench]', deviceInfo(), JSON.stringify(acc));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-1.5 pt-1.5 border-t border-green-500/30 text-cyan-400 min-w-0">
      <button
        className="flex items-center gap-2 cursor-pointer hover:text-cyan-300 text-left"
        onClick={run}
        title="On-device generation timing: construct → cold column → warm column → ingest. Fixed seed 12345 at column 0,0 so numbers compare across builds and devices"
      >
        <span className={`w-4 h-4 flex items-center justify-center shrink-0 ${running ? 'animate-pulse text-cyan-300' : 'text-cyan-400'}`}>
          <Timer size={14} />
        </span>
        <span>{running ? 'Running gen bench…' : 'Run gen bench'}</span>
      </button>
      {rows.map((r) => (
        <div key={r.label} className="mt-1 pb-1 border-b border-cyan-500/30">
          <div className="text-cyan-300 font-bold break-words">{r.label} — {r.chunks} chunks</div>
          <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-x-2">
            <div>cold:</div><div className="text-yellow-400">{r.genColdMs}</div>
            <div>warm:</div><div>{r.genWarmMs}</div>
            <div>ing 1:</div><div>{r.ingest1Ms}</div>
            <div>ing col:</div><div>{r.ingestAllMs}</div>
            <div>constr:</div><div>{r.constructMs}</div>
          </div>
        </div>
      ))}
      {rows.length > 0 && (
        <div className="text-[10px] text-cyan-500/70 break-all">{deviceInfo()}</div>
      )}
    </div>
  );
}

// ============== Dropdown option tables ==============

/** Voxel visualisation toggles (multi-select). Keys are VoxelDebugToggles fields. */
const VOXEL_TOGGLE_OPTIONS: readonly DropdownOption<keyof VoxelDebugToggles>[] = [
  { value: 'showChunkBounds', label: 'F1 bounds', title: 'Chunk boundary wireframes, sized to the chunk\'s LOD extent' },
  { value: 'showChunkCoords', label: 'F2 coords', title: 'Chunk labels: x,y,z,lod — only the LOD the visibility walk selected here' },
  { value: 'showWireframe', label: 'F3 wireframe', title: 'Wireframe on the terrain materials' },
  { value: 'seamSkirts', label: 'F5 skirts', title: 'Seam skirts AND their masks. Off restores the masked border bands, so this is a true A/B of the whole mechanism (default on)' },
  { value: 'seamDebug', label: 'F6 seam verts', title: 'Ring/rim curves the stitcher considered: green points+lines = in a curve, yellow points = considered then rejected' },
];

const QUALITY_OPTIONS: readonly DropdownOption<QualityLevel>[] =
  QUALITY_LEVELS.map((l) => ({ value: l, label: QUALITY_LABELS[l] }));

const BUILD_PREVIEW_OPTIONS: readonly DropdownOption<BuildPreviewLighting>[] =
  BUILD_PREVIEW_LIGHTING_ORDER.map((m) => ({ value: m, label: BUILD_PREVIEW_LIGHTING_LABELS[m] }));

// ============== Physics readout helpers ==============

/** Material name for a probe result; `—` when nothing was hit or the ray died in an unloaded chunk. */
function materialLabel(id: number): string {
  if (id < 0) return '—';
  return Materials.getName(id) ?? `#${id}`;
}

/**
 * One word for the player's state, worst-news-first. `swimming`/`wading` are DISPLAY-ONLY for now —
 * the thresholds are placeholders the real swimming physics will replace; nothing reads them yet.
 */
function physStateLabel(p: Readonly<PhysicsHud>): { label: string; tone: string } {
  if (p.stuck) return { label: 'STUCK', tone: 'text-red-400' };
  if (p.frozen) return { label: 'frozen', tone: 'text-yellow-400' };
  if (p.eyeInLiquid || (!p.grounded && p.submersionFraction >= 0.5)) {
    return { label: 'swimming', tone: 'text-blue-400' };
  }
  if (p.feetInLiquid) return { label: 'wading', tone: 'text-blue-300' };
  return { label: p.grounded ? 'grounded' : 'air', tone: '' };
}

/** Tooltips for the shader debug modes whose meaning isn't obvious from the label. */
const SHADER_MODE_TITLES: Partial<Record<TerrainDebugMode, string>> = {
  [TERRAIN_DEBUG_MODES.LOD]: 'Colour solid chunks by LOD, skirt strips red. Water and foliage keep their textures; liquid skirts are translucent red',
  [TERRAIN_DEBUG_MODES.SHADING_NORMAL]: 'The normal lighting actually uses, in world space — normal map applied. Unlike Normal (raw texture) or WorldNormal (geometry only), this is where a broken tangent frame shows up',
};

// ============== Main Debug Panel ==============

export function DebugPanel() {
  // Per-frame physics state lives outside Zustand (see state/transient.ts); it's read here
  // during the panel's own perf-driven re-render rather than pushed in at frame rate.
  const phys = getPhysicsHud();
  const physState = physStateLabel(phys);
  const {
    fps,
    perfStats,
    voxelDebug,
    voxelStats,
    toggleVoxelDebug,
    textureState,
    terrainDebugMode,
    stepTerrainDebugMode,
    setTerrainDebugMode,
    buildPreviewLighting,
    setBuildPreviewLighting,
    environment,
    setEnvironment,
    materialSettings,
    waterSettings,
    debugPanelSections,
    toggleDebugSection,
    // Quality state
    qualityLevel,
    quality,
    fov,
    renderScale,
    setRenderScale,
    msaaSamples,
    setMsaaSamples,
    dayNightConfig,
    updateKeyframe,
    setDayNightConfig,
  } = useGameStore();

  // Shader-mode options in the SELECTOR's order (mode numbers are shader branches, not an order).
  const shaderOptions: DropdownOption<TerrainDebugMode>[] = TERRAIN_DEBUG_MODE_ORDER.map((m) => ({
    value: m,
    label: TERRAIN_DEBUG_MODE_NAMES[m],
    ...(SHADER_MODE_TITLES[m] ? { title: SHADER_MODE_TITLES[m] } : {}),
  }));

  // Whether the live quality still matches the selected preset (ignoring view distance).
  const isCustomQuality = !qualityMatchesPreset(quality, qualityLevel);

  // Which keyframe is being edited in the keyframe editor.
  const [kfIndex, setKfIndex] = useState(0);
  const keyframe = dayNightConfig.keyframes[kfIndex];

  /** Edit the selected keyframe and re-derive the cycle live (no reset). */
  const editKeyframe = (u: Partial<DayNightKeyframe>) => { updateKeyframe(kfIndex, u); invalidateDayNight(); };
  /** Edit a global cycle setting (sun/moon appearance, arc, timing) and re-derive live. */
  const editGlobal = (u: Partial<Omit<DayNightConfig, 'keyframes'>>) => { setDayNightConfig(u); invalidateDayNight(); };

  const [cacheClearing, setCacheClearing] = useState(false);

  const handleClearTextureCache = async () => {
    setCacheClearing(true);
    await textureCache.clearCache();
    setCacheClearing(false);
    console.log('Texture cache cleared - reload page to re-download');
  };

  const handleCycleQuality = () => {
    // Read from store directly to avoid stale closure in F8 event handler
    const currentLevel = useGameStore.getState().qualityLevel;
    const currentVisibility = useGameStore.getState().quality.visibilityRadius;
    const next = cycleQualityLevel(currentLevel);
    syncPresetToStore(next, currentVisibility);
  };

  /** Apply a full quality preset and sync all individual settings to store */
  const syncPresetToStore = (level: QualityLevel, customVisibility?: number) => {
    syncQualityToStore(level, customVisibility);
  };

  // Apply environment changes to the scene. Only the CHANGED fields are applied — never a
  // re-push of the whole environment — so editing one setting can't clobber the day-night
  // cycle's animated sun/moon/hemisphere state (the old reset bug).
  const handleEnvironmentChange = (updates: Partial<EnvironmentSettings>) => {
    setEnvironment(updates);
    applyEnvironmentSettings(updates);

    // SSAO/bloom/godrays/saturation are all store-driven via effects.ts — no direct call needed.

    // Apply voxel light-fill curves to terrain materials
    if ('skyFillPower' in updates || 'blockFillPower' in updates) {
      applyLightFillSettings(updates);
    }

    // Apply block-light (emitter) colour/intensity to terrain materials (live, no remesh)
    if ('blockLightColor' in updates || 'blockLightIntensity' in updates) {
      applyBlockLightSettings(updates);
    }
  };

  // Apply material settings changes to shaders
  const handleMaterialChange = (updates: Partial<typeof materialSettings>) => {
    useGameStore.getState().setMaterialSettings(updates);
  };

  // Reset material settings to defaults
  const handleResetMaterialSettings = () => {
    useGameStore.getState().resetMaterialSettings();
  };

  // Apply water settings changes to shaders
  const handleWaterChange = (updates: Partial<typeof waterSettings>) => {
    useGameStore.getState().setWaterSettings(updates);
  };

  // Reset water settings to defaults
  const handleResetWaterSettings = () => {
    useGameStore.getState().resetWaterSettings();
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      switch (e.key) {
        case 'F1':
          e.preventDefault();
          toggleVoxelDebug('showChunkBounds');
          break;
        case 'F2':
          e.preventDefault();
          toggleVoxelDebug('showChunkCoords');
          break;
        case 'F3':
          e.preventDefault();
          toggleVoxelDebug('showWireframe');
          break;
        case 'F5':
          e.preventDefault();
          toggleVoxelDebug('seamSkirts');
          break;
        case 'F6':
          e.preventDefault();
          toggleVoxelDebug('seamDebug');
          break;
        case 'F7':
          e.preventDefault();
          stepTerrainDebugMode(1);
          break;
        case 'F8':
          e.preventDefault();
          stepTerrainDebugMode(-1);
          break;
        case 'F9':
          e.preventDefault();
          handleCycleQuality();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleVoxelDebug, stepTerrainDebugMode]);
  
  // Sync terrain debug mode to shader. The LOD view additionally swaps per-LOD material variants
  // onto the chunk meshes — a shared uniform cannot say which LOD a mesh is.
  useEffect(() => {
    setShaderDebugMode(terrainDebugMode);
    setLodDebugEnabled(terrainDebugMode === TERRAIN_DEBUG_MODES.LOD);
  }, [terrainDebugMode]);

  // Shadow map size options (advanced/manual — quality preset drives the applied value)
  const shadowMapOptions = [
    { label: '512', value: 512 },
    { label: '1024', value: 1024 },
    { label: '2048', value: 2048 },
    { label: '4096', value: 4096 },
  ];

  const debugPanelExpanded = useGameStore((s) => s.debugPanelExpanded);
  const toggleDebugPanelExpanded = useGameStore((s) => s.toggleDebugPanelExpanded);

  // Compact mode: just FPS with expand button
  if (!debugPanelExpanded) {
    return (
      <div
        className="absolute top-5 left-5 py-1.5 px-3 bg-black/80 text-green-500 font-mono text-xs rounded-lg cursor-pointer hover:bg-black/90 select-none pointer-events-auto flex items-center gap-2"
        onClick={toggleDebugPanelExpanded}
        title="Click to expand debug panel"
      >
        <span className={fps < 30 ? 'text-red-400' : fps < 55 ? 'text-yellow-400' : ''}>{fps} FPS</span>
        <ChevronRight size={13} className="text-green-500/50" />
      </div>
    );
  }

  return (
    <div className="absolute top-5 left-5 bg-black/80 text-green-500 font-mono text-xs rounded-lg max-h-[calc(100dvh-200px)] w-[15rem] pointer-events-auto flex flex-col overflow-hidden">

      {/* FPS + collapse — pinned at the top (does not scroll). */}
      <button
        onClick={toggleDebugPanelExpanded}
        className="shrink-0 flex items-center justify-between px-4 pt-2 pb-1.5 cursor-pointer text-green-500 hover:text-green-300"
        title="Collapse debug panel"
      >
        <span className={`font-bold ${fps < 30 ? 'text-red-400' : fps < 55 ? 'text-yellow-400' : ''}`}>{fps} FPS</span>
        <ChevronDown size={13} />
      </button>

      {/* Scrollable body — starts below the FPS row, thin scrollbar. */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-compact px-4 pb-2.5">

      {/* ============== PERFORMANCE SECTION (client-side only) ============== */}
      {/* FPS is shown in the sticky header above — not duplicated here. */}
      <Section
        title="Performance"
        icon={<Zap size={13} />}
        isOpen={debugPanelSections.performance}
        onToggle={() => toggleDebugSection('performance')}
        color="cyan"
      >
        {/* THE FRAME IN THREE TIERS (see PerformanceStats): the measured interval; the strip — main
            thread and GPU side by side on one time axis, because they run in parallel and the interval
            is set by whichever reaches it; then the World against its allowance, and the stages ranked
            by peak. Read top-down: GPU bar at the interval → GPU-bound, nothing below matters; CPU bar
            there → which segment; World over its allowance → which stage's peak. */}
        <div className="text-cyan-400">
          <div className="flex justify-between">
            <span title="rAF interval, ms — the frame as the display sees it (rolling average)">Frame {perfStats.frameInterval.toFixed(1)}</span>
            <span title="VoxelWorld.update spend / this frame's allowance, ms. The allowance is refresh − the frame's other CPU − guard, clamped (voxel/frameBudget.ts). Red = overrunning">
              <span className={STAGE_COLOR.world}>World</span>{' '}
              <span className={perfStats.voxelUpdate > perfStats.voxelBudget ? 'text-red-400' : ''}>{perfStats.voxelUpdate.toFixed(1)}/{perfStats.voxelBudget.toFixed(1)}</span>
            </span>
            <button onClick={resetPerfPeaks} className="text-cyan-500/70 hover:text-cyan-300 cursor-pointer" title="Reset the session peaks (the table's sort key and peak values)">↺</button>
          </div>
          <FrameStrip p={perfStats} />
          <StageTable p={perfStats} />
        </div>
        {/* Two label/value pairs per row (4-col grid) — the panel is read on a phone, so height
            matters more than one-metric-per-line. Draw calls / triangles / textures / programs are
            deliberately absent: they measure the scene's size, not its cost, and never localized a
            problem. `Geom` stays as the GPU-buffer leak gauge (it must track the TerrainBatch's batch count). */}
        <div className="text-cyan-400 grid grid-cols-[auto_1fr_auto_1fr] gap-x-2">
          <div className="col-span-4 mt-1 pt-1 border-t border-cyan-500/30" />

          <div title="Voxel data resident in memory, all LODs — drawn or not. This is what physics reads">Chunks:</div>
          <div>{voxelStats.chunksLoaded}</div>
          <div title="Resident chunks that have built triangles (drawn or hidden behind a coarser LOD)">Meshed:</div>
          <div>{voxelStats.meshesVisible}</div>
          <div title="GPU geometries (renderer.info). Must track the TerrainBatch's batch count plus the preview/skybox/props — a residue growing over a session means leaked buffers">Geom:</div>
          <div>{perfStats.geometries}</div>
          {/* Queue and Parked are separate cells: the old "Q 0+234p" read as a backlog of 234 when
              parked entries are residency, and the subtraction hid the number that matters. */}
          <div title="Chunks waiting to be meshed. Should drain to 0">Queue:</div>
          <div className={perfStats.remeshQueueSize - perfStats.remeshParked > 10 ? 'text-yellow-400' : ''}>
            {perfStats.remeshQueueSize - perfStats.remeshParked}
          </div>
          <div title="Queued but deferred by design — margin/occluder chunks that will not mesh until the view reaches them. Not a backlog">Parked:</div>
          <div>{perfStats.remeshParked}</div>
          <div title="Chunks requested from the server whose data has not arrived yet">Pend:</div>
          <div>{perfStats.pendingChunks}</div>
          {/* The RATIO is the diagnostic, and it has to be dimensionless to be readable: dispatches
              per DISTINCT chunk ever meshed, so 1.0 really is "each chunk meshed once". */}
          <div title="Mesh dispatches per distinct chunk ever meshed. 1.0x = each meshed once; above ~1.2x is re-meshing">Churn:</div>
          <div className={Number.parseFloat(voxelStats.meshChurn) > 1.2 ? 'text-yellow-400' : ''}>
            {voxelStats.meshChurn}
          </div>
          {/* Only NEVER-shown meshes count: a mesh shown once and then superseded by a finer LOD is
              the staircase working on purpose, and counting it here read as a fault. */}
          <div title="Share of built meshes never shown — geometry built for nothing. Should trend to 0%">Waste:</div>
          <div className={Number.parseInt(voxelStats.wastePct, 10) > 10 ? 'text-yellow-400' : ''}>
            {voxelStats.wastePct}
          </div>
          <div title="Draw calls this frame (renderer.info) — every mesh drawn, casters counted again on a shadow-map frame. ~28 µs of CPU submit each on the laptop">Calls:</div>
          <div className={perfStats.drawCalls > 300 ? 'text-yellow-400' : ''}>{perfStats.drawCalls}</div>
          {/* THE PEAK'S SHAPE. A skirt spike is either one transaction naming many owners (tx) or a
              few owners that each cost a lot (Owner). The two rows tell them apart. */}
          <div title="Skirt owners this commit: built unbudgeted because a flip named them (p) / all distinct owners the commit named; then the most owners one transaction named, this commit / session peak">Skirt:</div>
          <div className={perfStats.skirt.maxTxOwners > 12 ? 'text-yellow-400' : ''}>
            {perfStats.skirt.priority}p/{perfStats.skirt.owners} tx{perfStats.skirt.maxTxOwners}/{perfStats.skirt.peakTxOwners}
          </div>
          <div title={`Owner build time this flush: total / the single most expensive owner, then the session's worst owner (${perfStats.skirt.peakOwnerKey || 'none yet'}) and worst flush total`}>Owner:</div>
          <div className={perfStats.skirt.ownerMaxMs > 4 ? 'text-yellow-400' : ''}>
            {perfStats.skirt.ownerMs.toFixed(1)}/{perfStats.skirt.ownerMaxMs.toFixed(1)} pk{perfStats.skirt.peakOwnerMs.toFixed(1)}/{perfStats.skirt.peakFlushMs.toFixed(0)}
          </div>
          {/* The emitter is the per-owner cost, so this is the count that matters: face×layer plane
              scans run, against the ones the empty-face cache answered without scanning. */}
          <div title="Seam emitter runs this flush: face×layer plane scans actually run / answered from the empty-face cache">Faces:</div>
          <div>{perfStats.skirt.facesEmitted}/{perfStats.skirt.facesSkipped}</div>
          {perfStats.jsHeapMB > 0 && (<>
            <div title="JS heap, MB (Chrome only)">Heap:</div><div>{perfStats.jsHeapMB}M</div>
          </>)}

          {/* ONE ROW FOR THE LADDER, spanning the full width and WRAPPING. Base, gate state, the
              three counts and the decimation deficit are all per level, and as four separate rows
              reading a fault meant looking the same level up four times — three of the panel's
              widest rows for one table. As its own grid cell with whitespace-nowrap it also set the
              panel's minimum width, making every other row twice as wide as it needed to be. */}
          {voxelStats.lodStatus && (
            <div
              className="col-span-4 mt-1 break-words"
              title={'Per LOD: [base] gate res/mesh/drawn -deficit.\n'
                + 'Gate — ✓ done, ▶ filling (u=unissued f=in-flight m=mesh work), · held by the ceiling, ▪ held by the floor.\n'
                + '-N = coarse chunks still drawing where finer ones belong (should fall to zero one level at a time).\n'
                + 'dec3→2 = stepping lod 3 down to 2. wd+n = watchdog force-opened a wedged level.'}
            >
              <span className="text-cyan-300">LOD </span>
              <span className={voxelStats.lodStatus.includes('wd+') ? 'text-yellow-400' : ''}>
                {voxelStats.lodStatus}
              </span>
            </div>
          )}
        </div>
      </Section>

      {/* ============== PHYSICS SECTION ============== */}
      {/* Fed by the per-frame field probes in GameCore.updateFieldProbes — these are voxel-FIELD
          reads (what the solver collides against), not collider or mesh state. */}
      <Section
        title="Physics"
        icon={<Footprints size={13} />}
        isOpen={debugPanelSections.physics}
        onToggle={() => toggleDebugSection('physics')}
        color="cyan"
      >
        <div className="text-cyan-400 grid grid-cols-[auto_1fr_auto_1fr] gap-x-2">
          <div title="STUCK = depenetration ladder exhausted · frozen = waiting on chunks · swimming/wading = capsule in liquid · grounded/air otherwise">State:</div>
          <div className={physState.tone}>{physState.label}</div>
          <div title="Surfaces the capsule touched this tick">Contact:</div>
          <div>
            {[phys.wallContact && 'wall', phys.hitCeiling && 'ceil', phys.steppedUp && 'step']
              .filter(Boolean).join(' ') || '—'}
          </div>
          <div title="Vertical velocity right now (negative = falling)">Vel Y:</div><div>{phys.velocityY.toFixed(1)}</div>
          <div title="Seconds of CONTINUOUS falling, frozen ticks excluded. Survives terminal-velocity clamping, which Vel Y does not">Fall T:</div>
          <div>{phys.fallTime.toFixed(1)}s</div>
          <div title="Ticks where the interior verifier failed and the depenetration ladder ran. Should stay 0 — anything else means terrain overlaps the capsule">Depen:</div>
          <div className={phys.depenetrations > 0 ? 'text-yellow-400' : ''}>{phys.depenetrations}</div>
          <div title="Horizontal motion was dropped this tick because it led into unloaded chunks (unknown is never treated as air)">Blocked:</div>
          <div className={phys.blockedByUnknown ? 'text-yellow-400' : ''}>{phys.blockedByUnknown ? 'unknown' : '—'}</div>

          <div className="col-span-4 mt-1 pt-1 border-t border-cyan-500/30" />

          {/* One pair per row: material names are long and the 4-column layout wrapped the labels. */}
          <div className="col-span-4 flex gap-2" title="Material of the solid under the player, from the collidable field — never the water or foliage being waded through">
            <span className="shrink-0">Ground M:</span>
            <span className="truncate">{materialLabel(phys.groundMaterial)}</span>
          </div>
          <div className="col-span-4 flex gap-2" title="Material a punch would hit: same ray, mask and reach as the real dig">
            <span className="shrink-0">Target M:</span>
            <span className="truncate">{materialLabel(phys.targetMaterial)}</span>
          </div>
          <div className="col-span-4 flex gap-2" title="Fraction of the capsule below the liquid surface, the liquid material, and which end is under">
            <span className="shrink-0">Water:</span>
            <span className="truncate">
              {phys.liquidMaterial >= 0 || phys.submersionFraction > 0
                ? `${phys.submersionFraction.toFixed(2)} ${materialLabel(phys.liquidMaterial)}`
                  + `${phys.eyeInLiquid ? ' eye' : phys.feetInLiquid ? ' feet' : ''}`
                : '—'}
            </span>
          </div>
        </div>
      </Section>

      {/* ============== DEBUG SECTION ============== */}
      <Section
        title="Debug"
        icon={<Wrench size={13} />}
        isOpen={debugPanelSections.debug}
        onToggle={() => toggleDebugSection('debug')}
        color="yellow"
      >
        {/* Uniform dropdowns, no sub-headings. Voxel is multi-select; the rest pick one. */}
        <Dropdown
          label="Voxel"
          values={VOXEL_TOGGLE_OPTIONS.filter((o) => voxelDebug[o.value]).map((o) => o.value)}
          options={VOXEL_TOGGLE_OPTIONS}
          onToggle={(k) => toggleVoxelDebug(k)}
        />
        <Dropdown
          label="Shader F7/8"
          value={terrainDebugMode}
          options={shaderOptions}
          onSelect={(m) => setTerrainDebugMode(m)}
          title="Terrain shader debug view — F7 next, F8 previous"
        />
        {terrainDebugMode === TERRAIN_DEBUG_MODES.LOD && (
          <>
            <div className="mb-0.5 flex flex-wrap gap-x-1.5 text-[10px]">
              {LOD_DEBUG_COLORS.map((_, lod) => (
                <span key={lod} className="flex items-center gap-0.5">
                  <span
                    className="inline-block w-2 h-2 rounded-sm border border-black/40"
                    style={{ backgroundColor: lodDebugCss(lod as LodTag) }}
                  />
                  <span className="text-yellow-300/70">{lodDebugLabel(lod as LodTag)}</span>
                </span>
              ))}
            </div>
            {/* The seam kinds WITH THEIR TRIANGLE COUNTS. Two hues 20 degrees apart in a palette this
                crowded are a guess; a kind reading 0 is the only proof it is not on screen. */}
            <div className="mb-0.5 flex flex-wrap gap-x-1.5 text-[10px]">
              {SKIRT_KIND_NAMES.slice(1).map((name, i) => {
                const n = Number(voxelStats.skirtKinds.split(' ')[i] ?? 0);
                return (
                  <span key={name} className="flex items-center gap-0.5" title={`seam kind ${i + 1}`}>
                    <span
                      className="inline-block w-2 h-2 rounded-sm border border-black/40"
                      style={{ backgroundColor: skirtKindCss(i + 1) }}
                    />
                    <span className={n > 0 ? 'text-yellow-300/70' : 'text-yellow-300/25'}>
                      {name} {n >= 10000 ? `${Math.round(n / 1000)}k` : n}
                    </span>
                  </span>
                );
              })}
            </div>
          </>
        )}
        <Dropdown
          label="Preview"
          value={buildPreviewLighting}
          options={BUILD_PREVIEW_OPTIONS}
          onSelect={(m) => setBuildPreviewLighting(m)}
          title="Build-preview lighting: Off (light only after commit), Deferred (spill at settle), Full (real-time spill)"
        />

        <div className="mt-1.5 pt-1.5 border-t border-green-500/30 text-yellow-400">
          <a href="/materials" className="block text-cyan-500 hover:text-cyan-300 underline" title="Material pallet viewer">
            Textures: {textureState}
          </a>
          <button
            className="flex items-center gap-2 cursor-pointer hover:text-yellow-300"
            onClick={handleClearTextureCache}
          >
            <span className={`w-4 h-4 flex items-center justify-center text-red-400 ${cacheClearing ? 'animate-pulse' : ''}`}>
              <Trash2 size={14} />
            </span>
            <span>Clear Texture Cache</span>
          </button>
        </div>

        <GenTimingBench />
      </Section>

      {/* ============== QUALITY SECTION ============== */}
      <Section
        title="Quality"
        icon={<Sliders size={13} />}
        isOpen={debugPanelSections.quality}
        onToggle={() => toggleDebugSection('quality')}
        color="yellow"
      >
        {/* Preset selector */}
        <div className="mb-2">
          <Dropdown
            label="Preset"
            value={qualityLevel}
            options={QUALITY_OPTIONS}
            onSelect={(l) => syncPresetToStore(l, quality.visibilityRadius)}
            title="Quality preset (F9 cycles)"
          />
          {isCustomQuality && (
            <div className="text-[10px] text-orange-400" title="Live settings no longer match the preset">
              Custom
            </div>
          )}
        </div>

        {/* Preset-driven levers — one segmented control each */}
        <div className="mb-2 pt-2 border-t border-yellow-500/30">
          {QUALITY_ROWS.map((row) => (
            <SegmentedRow
              key={row.key}
              label={row.label}
              segments={row.segments}
              active={row.match(quality)}
              onSelect={(i) => applyQualityPatch(row.segments[i].patch)}
            />
          ))}
        </div>

        {/* Independent of preset */}
        <div className="mb-2 pt-2 border-t border-yellow-500/30">
          <div className="text-yellow-400 text-xs mb-1 font-bold">Independent of preset</div>
          <SegmentedRow
            label="MSAA"
            segments={MSAA_OPTIONS}
            active={MSAA_OPTIONS.findIndex((o) => o.value === msaaSamples)}
            onSelect={(i) => setMsaaSamples(MSAA_OPTIONS[i].value)}
          />
          <Slider
            label="Resolution"
            value={Math.round(renderScale * 100)}
            min={25}
            max={100}
            step={5}
            onChange={(v) => setRenderScale(v / 100)}
            formatValue={(v) => `${v}%`}
          />
          <Slider
            label="FoV"
            value={fov}
            min={75}
            max={120}
            step={1}
            onChange={(v) => {
              useGameStore.getState().setFov(v);
              const cam = getCamera();
              if (cam) { cam.fov = v; cam.updateProjectionMatrix(); }
            }}
            formatValue={(v) => `${v}°`}
          />
        </div>

        {/* Fine-tuning */}
        <div className="mb-2 pt-2 border-t border-yellow-500/30">
          <div className="text-yellow-400 text-xs mb-1 font-bold">Fine-tuning</div>
          <Slider
            label="Shadow Softness (texels @4096)"
            value={environment.shadowBlurRadius}
            min={1}
            max={25}
            step={1}
            onChange={(v) => handleEnvironmentChange({ shadowBlurRadius: v })}
          />
        </div>
      </Section>

      {/* ============== MATERIALS SECTION ============== */}
      <Section
        title="Materials"
        icon={<Palette size={13} />}
        isOpen={debugPanelSections.materials}
        onToggle={() => toggleDebugSection('materials')}
        color="yellow"
      >
        {/* Texture Multipliers */}
        <div className="mb-3">
          <div className="text-yellow-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Sliders size={12} /> Texture Adjustments</div>
          <Slider
            label="Roughness"
            value={materialSettings.roughnessMultiplier}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => handleMaterialChange({ roughnessMultiplier: v })}
          />
          <Slider
            label="Metalness Offset"
            value={materialSettings.metalnessOffset}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => handleMaterialChange({ metalnessOffset: v })}
          />
          <Slider
            label="AO Intensity"
            value={materialSettings.aoIntensity}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => handleMaterialChange({ aoIntensity: v })}
          />
          <Slider
            label="Normal Strength"
            value={materialSettings.normalStrength}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => handleMaterialChange({ normalStrength: v })}
          />
        </div>

        {/* Foliage (transparent layer) lighting — see TerrainMaterial FOLIAGE_LIGHTING */}
        <div className="mb-3 pt-2 border-t border-yellow-500/30">
          <div className="text-yellow-400 text-xs mb-1 font-bold">Foliage Lighting</div>
          <Slider
            label="Light Wrap"
            value={materialSettings.foliageTranslucency}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => handleMaterialChange({ foliageTranslucency: v })}
          />
          <Slider
            label="Underside Sky Bias"
            value={materialSettings.foliageSkyBias}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => handleMaterialChange({ foliageSkyBias: v })}
          />
        </div>
        
        {/* Tri-planar Blending */}
        <div className="mb-3 pt-2 border-t border-yellow-500/30">
          <div className="text-yellow-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Grid3x3 size={12} /> Tri-Planar Blending</div>
          <Slider
            label="Blend Sharpness"
            value={materialSettings.blendSharpness}
            min={1}
            max={8}
            step={0.1}
            onChange={(v) => handleMaterialChange({ blendSharpness: v })}
          />
          <Slider
            label="Repeat Scale"
            value={materialSettings.repeatScale}
            min={0.5}
            max={4}
            step={0.1}
            onChange={(v) => handleMaterialChange({ repeatScale: v })}
          />
          <Slider
            label="LOD Scale First LOD"
            value={materialSettings.lodTexFirstLod}
            min={1}
            max={6}
            step={1}
            onChange={(v) => handleMaterialChange({ lodTexFirstLod: v })}
          />
          <Slider
            label="LOD Scale Per Level"
            value={materialSettings.lodTexPerLod}
            min={1}
            max={3}
            step={0.1}
            onChange={(v) => handleMaterialChange({ lodTexPerLod: v })}
          />
        </div>
        
        {/* Wind Animation */}
        <div className="mb-3 pt-2 border-t border-yellow-500/30">
          <div className="text-yellow-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Wind size={12} /> Wind Animation</div>
          <Slider
            label="Strength"
            value={materialSettings.windStrength}
            min={0}
            max={0.5}
            step={0.01}
            onChange={(v) => handleMaterialChange({ windStrength: v })}
          />
          <Slider
            label="Speed"
            value={materialSettings.windSpeed}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => handleMaterialChange({ windSpeed: v })}
          />
          <Slider
            label="Frequency"
            value={materialSettings.windFrequency}
            min={0.1}
            max={3}
            step={0.1}
            onChange={(v) => handleMaterialChange({ windFrequency: v })}
          />
        </div>
        
        {/* Reset Button */}
        <div className="pt-2 border-t border-yellow-500/30">
          <button
            onClick={handleResetMaterialSettings}
            className="w-full py-1 px-2 bg-yellow-900/50 hover:bg-yellow-800/50 text-yellow-400 rounded text-xs"
          >
            Reset to Defaults
          </button>
        </div>
      </Section>

      {/* ============== WATER SECTION ============== */}
      <Section
        title="Water"
        icon={<Droplet size={13} />}
        isOpen={debugPanelSections.water ?? false}
        onToggle={() => toggleDebugSection('water')}
        color="cyan"
      >
        {/* Wave Animation */}
        <div className="mb-3">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Waves size={12} /> Waves</div>
          <Slider
            label="Amplitude"
            value={waterSettings.waveAmplitude}
            min={0}
            max={0.5}
            step={0.01}
            onChange={(v) => handleWaterChange({ waveAmplitude: v })}
          />
          <Slider
            label="Frequency"
            value={waterSettings.waveFrequency}
            min={0.1}
            max={3}
            step={0.1}
            onChange={(v) => handleWaterChange({ waveFrequency: v })}
          />
          <Slider
            label="Speed"
            value={waterSettings.waveSpeed}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => handleWaterChange({ waveSpeed: v })}
          />
        </div>
        
        {/* Surface Effects */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Sparkles size={12} /> Surface</div>
          <Slider
            label="Normal Strength"
            value={waterSettings.normalStrength}
            min={0.5}
            max={5}
            step={0.1}
            onChange={(v) => handleWaterChange({ normalStrength: v })}
          />
          <Slider
            label="Normal Scale"
            value={waterSettings.normalScale}
            min={0.1}
            max={3}
            step={0.1}
            onChange={(v) => handleWaterChange({ normalScale: v })}
          />
          <Slider
            label="Scatter Strength"
            value={waterSettings.scatterStrength}
            min={0}
            max={3}
            step={0.1}
            onChange={(v) => handleWaterChange({ scatterStrength: v })}
          />
          <Slider
            label="Scatter Scale"
            value={waterSettings.scatterScale}
            min={0}
            max={5}
            step={0.05}
            onChange={(v) => handleWaterChange({ scatterScale: v })}
          />
          <Slider
            label="Roughness"
            value={waterSettings.roughness}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => handleWaterChange({ roughness: v })}
          />
          <Slider
            label="Fresnel Power"
            value={waterSettings.fresnelPower}
            min={1}
            max={8}
            step={0.1}
            onChange={(v) => handleWaterChange({ fresnelPower: v })}
          />
          <Slider
            label="Opacity"
            value={waterSettings.waterOpacity}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => handleWaterChange({ waterOpacity: v })}
          />
        </div>
        
        {/* Color Tint */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Palette size={12} /> Tint</div>
          <Slider
            label="Red"
            value={waterSettings.waterTint[0]}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => handleWaterChange({ waterTint: [v, waterSettings.waterTint[1], waterSettings.waterTint[2]] })}
          />
          <Slider
            label="Green"
            value={waterSettings.waterTint[1]}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => handleWaterChange({ waterTint: [waterSettings.waterTint[0], v, waterSettings.waterTint[2]] })}
          />
          <Slider
            label="Blue"
            value={waterSettings.waterTint[2]}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => handleWaterChange({ waterTint: [waterSettings.waterTint[0], waterSettings.waterTint[1], v] })}
          />
        </div>
        
        {/* Reset Button */}
        <div className="pt-2 border-t border-cyan-500/30">
          <button
            onClick={handleResetWaterSettings}
            className="w-full py-1 px-2 bg-cyan-900/50 hover:bg-cyan-800/50 text-cyan-400 rounded text-xs"
          >
            Reset to Defaults
          </button>
        </div>
      </Section>

      {/* ============== DAY-NIGHT CYCLE SECTION ============== */}
      <Section
        title="Day-Night Cycle"
        icon={<Moon size={13} />}
        isOpen={debugPanelSections.dayNightCycle ?? false}
        onToggle={() => toggleDebugSection('dayNightCycle')}
        color="cyan"
      >
        {/* Master Toggle */}
        <div className="mb-3">
          <Toggle
            label="Enable Cycle"
            value={environment.dayNightEnabled ?? false}
            onChange={(v) => handleEnvironmentChange({ dayNightEnabled: v })}
          />
        </div>

        {/* Time Display and Controls */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Clock size={12} /> Time</div>
          <div className="text-center mb-1">
            <span className="text-yellow-400 text-lg">{formatTimeOfDay(environment.timeOfDay)}</span>
            <span className="text-cyan-300 text-xs ml-2">{getDayPhaseLabel(environment.timeOfDay)}</span>
          </div>
          <Slider
            label="Time of Day"
            value={environment.timeOfDay}
            min={0}
            max={1}
            step={0.001}
            onChange={(v) => handleEnvironmentChange({ timeOfDay: v })}
            formatValue={(v) => formatTimeOfDay(v)}
          />
          <Slider
            label="Speed (min/s)"
            value={environment.timeSpeed}
            min={0}
            max={60}
            step={1}
            onChange={(v) => handleEnvironmentChange({ timeSpeed: v })}
            formatValue={(v) => v === 0 ? 'Paused' : `${(v ?? 0).toFixed(0)}`}
          />
        </div>

        {/* Sun — global appearance + arc (position follows the day-length arc) */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Sun size={12} /> Sun</div>
          <Slider label="Height" value={dayNightConfig.sunHeight} min={10} max={90} step={1} onChange={(v) => editGlobal({ sunHeight: v })} formatValue={(v) => `${(v ?? 0).toFixed(0)}°`} />
          <Slider label="Distance" value={dayNightConfig.sunDistance} min={50} max={400} step={10} onChange={(v) => editGlobal({ sunDistance: v })} formatValue={(v) => `${(v ?? 0).toFixed(0)}`} />
          <Slider label="Size" value={dayNightConfig.sunSize} min={0.2} max={4} step={0.05} onChange={(v) => editGlobal({ sunSize: v })} />
          <Slider label="Intensity" value={dayNightConfig.sunIntensity} min={0} max={10} step={0.1} onChange={(v) => editGlobal({ sunIntensity: v })} />
        </div>

        {/* Moon — its own global appearance + arc */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Moon size={12} /> Moon</div>
          <Slider label="Height" value={dayNightConfig.moonHeight} min={10} max={90} step={1} onChange={(v) => editGlobal({ moonHeight: v })} formatValue={(v) => `${(v ?? 0).toFixed(0)}°`} />
          <Slider label="Distance" value={dayNightConfig.moonDistance} min={50} max={400} step={10} onChange={(v) => editGlobal({ moonDistance: v })} formatValue={(v) => `${(v ?? 0).toFixed(0)}`} />
          <Slider label="Size" value={dayNightConfig.moonSize} min={0.2} max={4} step={0.05} onChange={(v) => editGlobal({ moonSize: v })} />
          <Slider label="Intensity" value={dayNightConfig.moonIntensity} min={0} max={3} step={0.05} onChange={(v) => editGlobal({ moonIntensity: v })} />
        </div>

        {/* Timing — dawn/dusk transition windows; day & night hold between them */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Clock size={12} /> Timing</div>
          <Slider label="Sunrise Start" value={dayNightConfig.sunriseStart} min={0} max={0.999} step={0.001} onChange={(v) => editGlobal({ sunriseStart: v })} formatValue={(v) => formatTimeOfDay(v)} />
          <Slider label="Sunrise End" value={dayNightConfig.sunriseEnd} min={0} max={0.999} step={0.001} onChange={(v) => editGlobal({ sunriseEnd: v })} formatValue={(v) => formatTimeOfDay(v)} />
          <Slider label="Sunset Start" value={dayNightConfig.sunsetStart} min={0} max={0.999} step={0.001} onChange={(v) => editGlobal({ sunsetStart: v })} formatValue={(v) => formatTimeOfDay(v)} />
          <Slider label="Sunset End" value={dayNightConfig.sunsetEnd} min={0} max={0.999} step={0.001} onChange={(v) => editGlobal({ sunsetEnd: v })} formatValue={(v) => formatTimeOfDay(v)} />
          <Slider label="Twilight" value={dayNightConfig.twilightAngle} min={1} max={20} step={0.5} onChange={(v) => editGlobal({ twilightAngle: v })} formatValue={(v) => `${(v ?? 0).toFixed(1)}°`} />
        </div>

        {/* Voxel light fill curves + block light */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Lightbulb size={12} /> Voxel Light</div>
          <Slider label="Sky Fill Power" value={environment.skyFillPower} min={0.25} max={2} step={0.05} onChange={(v) => handleEnvironmentChange({ skyFillPower: v })} />
          <Slider label="Block Fill Power" value={environment.blockFillPower} min={0.25} max={4} step={0.05} onChange={(v) => handleEnvironmentChange({ blockFillPower: v })} />
          <ColorPicker label="Block Color" value={environment.blockLightColor} onChange={(v) => handleEnvironmentChange({ blockLightColor: v })} />
          <Slider label="Block Intensity" value={environment.blockLightIntensity} min={0} max={4} step={0.05} onChange={(v) => handleEnvironmentChange({ blockLightIntensity: v })} />
        </div>

        {/* Shadows */}
        <div className="mb-3 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Moon size={12} /> Shadows</div>
          <Slider label="Bias" value={environment.shadowBias} min={-0.01} max={0.01} step={0.0001} onChange={(v) => handleEnvironmentChange({ shadowBias: v })} formatValue={(v) => v.toFixed(4)} />
          <Slider label="Normal Bias" value={environment.shadowNormalBias} min={0} max={0.1} step={0.001} onChange={(v) => handleEnvironmentChange({ shadowNormalBias: v })} formatValue={(v) => v.toFixed(3)} />
          <Select label="Map Size" value={environment.shadowMapSize} options={shadowMapOptions} onChange={(v) => handleEnvironmentChange({ shadowMapSize: v })} />
        </div>

        {/* Keyframe editor — pick a phase palette, edit its colours + fill */}
        <div className="mb-2 pt-2 border-t border-cyan-500/30">
          <div className="text-cyan-400 text-xs mb-1 font-bold flex items-center gap-1.5"><Sunrise size={12} /> Palettes</div>
          <div className="flex gap-1 mb-2">
            {dayNightConfig.keyframes.map((k, i) => (
              <button
                key={k.name}
                onClick={() => setKfIndex(i)}
                className={`flex-1 px-1 py-1 text-[11px] rounded cursor-pointer ${
                  i === kfIndex ? 'bg-cyan-600 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                {k.name}
              </button>
            ))}
          </div>
          {keyframe && (
            <>
              <ColorPicker label="Sun Color" value={keyframe.sunColor} onChange={(v) => editKeyframe({ sunColor: v })} />
              <ColorPicker label="Moon Color" value={keyframe.moonColor} onChange={(v) => editKeyframe({ moonColor: v })} />
              <ColorPicker label="Sky Zenith" value={keyframe.skyZenithColor} onChange={(v) => editKeyframe({ skyZenithColor: v })} />
              <ColorPicker label="Sky Horizon" value={keyframe.skyHorizonColor} onChange={(v) => editKeyframe({ skyHorizonColor: v })} />
              <ColorPicker label="Ground" value={keyframe.groundColor} onChange={(v) => editKeyframe({ groundColor: v })} />
              <Slider label="Fill Intensity" value={keyframe.hemisphereIntensity} min={0} max={5} step={0.1} onChange={(v) => editKeyframe({ hemisphereIntensity: v })} />
            </>
          )}
        </div>
      </Section>

      {/* ============== POST-PROCESSING SECTION ============== */}
      <Section
        title="Post-Processing"
        icon={<Sparkles size={13} />}
        isOpen={debugPanelSections.environment}
        onToggle={() => toggleDebugSection('environment')}
        color="cyan"
      >
        <Slider label="SSAO Intensity" value={environment.ssaoIntensity} min={0} max={10} step={0.1} onChange={(v) => handleEnvironmentChange({ ssaoIntensity: v })} />
        <Slider label="SSAO Radius" value={environment.ssaoRadius} min={0} max={0.5} step={0.01} onChange={(v) => handleEnvironmentChange({ ssaoRadius: v })} />
        <Slider label="Bloom Intensity" value={environment.bloomIntensity} min={0} max={3} step={0.05} onChange={(v) => handleEnvironmentChange({ bloomIntensity: v })} />
        <Slider label="Bloom Threshold" value={environment.bloomThreshold} min={0} max={1} step={0.01} onChange={(v) => handleEnvironmentChange({ bloomThreshold: v })} />
        <Slider label="Bloom Radius" value={environment.bloomRadius} min={0} max={3} step={0.01} onChange={(v) => handleEnvironmentChange({ bloomRadius: v })} />
        <Slider label="God Rays Decay" value={environment.godRaysDecay} min={0} max={1} step={0.01} onChange={(v) => handleEnvironmentChange({ godRaysDecay: v })} />
        <Slider label="God Rays Exposure" value={environment.godRaysExposure} min={0} max={1} step={0.01} onChange={(v) => handleEnvironmentChange({ godRaysExposure: v })} />
        <Slider label="Saturation" value={environment.saturation} min={0} max={2} step={0.05} onChange={(v) => handleEnvironmentChange({ saturation: v })} />
      </Section>
      </div>
    </div>
  );
}
